import {
  Button,
  ButtonType,
  ConfirmationModal,
  FlexProps,
  FormDropdown,
  Hr,
} from '@access-erp/ui-components';
import { yupResolver } from '@hookform/resolvers/yup';
import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import useGetContractsCount from '../../hooks/useGetContractsCount';
import { CustomerOutputWithId } from '../../hooks/useGetCustomers';
import useSearchCustomers from '../../hooks/useSearchCustomers';
import useUpdateParentCustomer from '../../hooks/useUpdateParentCustomer';

import styles from './edit-parent-customer.module.scss';
import { CustomerOption, validationSchema } from './validationSchema';
import { FormattingUtils } from '@/utils/Formatting.utils';

interface AmendDatesProps {
  onClose: () => void;
  isVisible: boolean;
  selectedRow: CustomerOutputWithId;
  refreshGrid: () => void;
}

export function EditParentCustomerModal({
  onClose,
  isVisible,
  selectedRow,
  refreshGrid,
}: AmendDatesProps) {
  const methods = useForm({
    defaultValues: {
      parentCustomerID: selectedRow.parentCustomerName,
    },
    resolver: yupResolver(validationSchema),
    mode: 'onChange',
    context: selectedRow,
  });
  const parentCustomerId = selectedRow.parentCustomerID;
  const { searchCustomers, searchCustomersData } = useSearchCustomers();
  const { getContractsCount, contractsCount } = useGetContractsCount();
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const { updateParentCustomer } = useUpdateParentCustomer();
  const customers = searchCustomersData.map(
    (
      option: {
        customerName: any;
        customerAccountNumber: any;
        customerID: any;
      }
    ) => ({
      label: `${option?.customerName} (${option.customerAccountNumber})`,
      value: option.customerID,
    })
  );

  const currentParentData = selectedRow.parentCustomerID
    ? {
      value: selectedRow.parentCustomerID,
      label: `${selectedRow.parentCustomerName} (${selectedRow.parentCustomerAccountNumber})`,
    }
    : undefined;

  const changedParentCustomer = parentCustomerId && methods.getValues().parentCustomerID;
  const addedOrRemovedCustomerText = methods.getValues().parentCustomerID
    ? 'Adding a Parent Account'
    : 'Removing an existing Parent Account linkage';
  const warningModalTitle = changedParentCustomer
    ? 'Changing a Parent Account'
    : addedOrRemovedCustomerText;

  const onInputChange = (newValue: string) => {
    if (newValue.length > 0) {
      methods.setValue('parentCustomerID', newValue, {
        shouldDirty: false,
      });
      searchCustomers(newValue);
    }
  };

  const handleSave = async () => {
    if (typeof selectedRow.customerID === 'string') {
      await getContractsCount(selectedRow.customerID);
    }
    setShowConfirmationModal(true);
  };

  const handleRemoveParent = async () => {
    methods.setValue('parentCustomerID', null, {
      shouldDirty: false,
    });
    if (typeof selectedRow.customerID === 'string') {
      await getContractsCount(selectedRow.customerID);
    }
    setShowConfirmationModal(true);
  };

  const handleGridRefresh = () => {
    setShowConfirmationModal(false);
    onClose();
    refreshGrid();
  };
  const handleConfirm = () => {
    if (typeof selectedRow.customerID !== 'string') {
      // Optionally show an error or handle this case
      return;
    }
    updateParentCustomer(
      {
        customerID: selectedRow.customerID as string,
        parentCustomerID: methods.getValues().parentCustomerID,
      },
      handleGridRefresh,
    );
  };

  return (
        <>
            <ConfirmationModal
                open={isVisible}
                onClose={onClose}
                title={parentCustomerId ? 'Edit Parent Company' : 'Add Parent Company'}
                footerAlign={FlexProps['space-between']}
                hideFooter={true}
                width="small"
                id="confirmationModal"
            >
                <FormProvider {...methods}>
                    {parentCustomerId && (
                        <div className={styles.removeParent}>
                            <a className={styles.removeBtn} onClick={handleRemoveParent}>
                                Remove Parent
                            </a>
                        </div>
                    )}
                    <div>
                        <div className={styles['dropdown-margin']}>
                            <FormDropdown<CustomerOption>
                                label="Customers"
                                name="parentCustomerID"
                                id="parentCustomerId"
                                placeholder="Select customer"
                                onInputChange={onInputChange}
                                onChange={(newValue: string) => {
                                  methods.setValue('parentCustomerID', newValue, {
                                    shouldDirty: true,
                                    shouldValidate: true,
                                  });
                                }}
                                options={customers}
                                getOptionLabel={(option: CustomerOption) => option?.label}
                                getOptionValue={(option: CustomerOption) => option?.value}
                                defaultValue={currentParentData}
                                required={true}
                            />
                        </div>
                    </div>
                </FormProvider>

                <div style={{ margin: '20px 0' }}>
                    <Hr />
                </div>
                <div className={styles['modal-buttons']}>
                    <Button
                        wrapperStyle={{ width: 'initial', flex: 'none' }}
                        buttonType={ButtonType.Secondary}
                        text="Cancel"
                        onClick={onClose}
                    />
                    <Button
                        wrapperStyle={{ width: 'initial', flex: 'none' }}
                        buttonType={ButtonType.Primary}
                        text="Save"
                        onClick={handleSave}
                        disabled={!methods.formState.isDirty || !methods.formState.isValid}
                    />
                </div>
            </ConfirmationModal>

            <ConfirmationModal
                open={showConfirmationModal}
                title={warningModalTitle}
                onClose={() => setShowConfirmationModal(false)}
                onConfirm={handleConfirm}
            >
                {warningModalTitle} will impact all associated contracts. <br />
                This change will impact {contractsCount} {FormattingUtils.pluralize(contractsCount, 'contract')}. Please
                confirm you wish to continue.
            </ConfirmationModal>
        </>
  );
}
